/**
 * Where the season is.
 *
 * FOUR STOPS, NOT FIVE (Tim, 23 August). The strip used to read `Offseason → Keepers → Pre-draft
 * → Draft night → In-season`, and the Keepers stop was the odd one out: the other four are
 * stretches of the calendar, and that one was a TASK. It also lied about what the league is doing
 * — declaring keepers is one of half a dozen things happening between the championship game and
 * the rule-proposal deadline, and giving it a stop of its own made the app say the season had
 * moved on when all that had happened was a form opening.
 *
 * So the four are:
 *
 *   offseason    from the day after the championship game until rule proposals close. What the
 *                league does here is look back and write rules.
 *   pre-draft    from the proposal deadline to draft day. The committee, the keeper slates, the
 *                slot rankings, the lottery. This view HOLDS all the way to the draft — it does
 *                not empty itself as each deadline passes (Tim: "this view is in place all the
 *                way until draft day, even after deadlines have passed").
 *   draft day    the day either side of the draft date.
 *   in-season    everything after that.
 *
 * THE OFFSEASON HAS TWO FACES, and that is the thing the fifth stop was really trying to say. For
 * most of it the league is looking backwards — year in review, the history boards — and there is
 * nothing due. Two weeks out from the proposal deadline that inverts: the deadlines come to the
 * front as a pre-season checklist, rule submission becomes the headline, keeper thinking starts,
 * the year in review goes away and history drops to second billing. `offseasonFocus` is that
 * switch, and it is a FOCUS rather than a phase because the season has not moved — only what the
 * league owes has.
 *
 * DERIVED, NEVER STORED. A commissioner who moves the proposal deadline moves the phase with it,
 * and nobody has to remember to flip a switch. That rule survives from the first version and is
 * the reason none of this is in the database.
 */

export type Phase = 'offseason' | 'predraft' | 'draftday' | 'inseason';

/**
 * `label` is what a stop is called with the others beside it. `long` is what it is called when it
 * stands alone — on a phone the rail shows one name, and it has to name a time of year rather
 * than a section of the app.
 */
export const PHASES: ReadonlyArray<{ id: Phase; label: string; long: string }> = [
  { id: 'offseason', label: 'Offseason', long: 'Offseason' },
  { id: 'predraft', label: 'Pre-draft', long: 'Pre-draft' },
  { id: 'draftday', label: 'Draft day', long: 'Draft day' },
  { id: 'inseason', label: 'In-season', long: 'In-season' },
];

export const DAY_MS = 24 * 60 * 60 * 1000;

/** How long before the proposal deadline the offseason turns into a checklist. */
export const CHECKLIST_LEAD_DAYS = 14;

export interface PhaseInput {
  /** The boundary out of the offseason: rule proposals close and the league turns to the draft. */
  proposalDeadline?: string | null;
  committeeDate?: string | null;
  keeperDeadline?: string | null;
  draftDate?: string | null;
  /**
   * A commissioner freezing declarations early no longer moves the phase. It used to end the
   * keeper stop, and with that stop gone there is nothing for it to end — declarations closing is
   * a gate shutting inside pre-draft, not the season moving.
   */
  declarationsLocked?: boolean | null;
}

const at = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/*
 * DRAFT DAY IS A DATE, NOT A SWITCH. `draft.status === 'live'` was briefly allowed to force the
 * phase here, on the reasoning that a live board under another heading reads wrong. Tim's call,
 * and he is right: the strip states where the SEASON is, and the season does not move because
 * somebody opened the board — a commissioner testing the draft in August is still pre-draft, and
 * everyone else's screen should say so. That the draft is running is said loudly enough by the
 * live strip and the LIVE pill on the Draft item; it does not need a second voice here.
 */
export function phaseOf(s: PhaseInput | null | undefined, now = Date.now()): Phase {
  const draft = at(s?.draftDate);
  if (draft !== null) {
    if (now >= draft + DAY_MS) return 'inseason';
    if (now >= draft - DAY_MS) return 'draftday';
  }

  const proposals = at(s?.proposalDeadline);
  if (proposals !== null) return now >= proposals ? 'predraft' : 'offseason';

  /*
   * NO PROPOSAL DEADLINE ANNOUNCED, so the offseason has no gate of its own to end on. The
   * next-best evidence that the league has turned towards the draft, in the order it arrives:
   * the committee has met, the keeper deadline has passed, or there is a draft on the calendar
   * and nothing else at all — a league with a draft date and no preseason dates is mid-preseason,
   * not idle. Failing all three, nothing is happening.
   */
  const committee = at(s?.committeeDate);
  if (committee !== null && now >= committee) return 'predraft';
  const keeper = at(s?.keeperDeadline);
  if (keeper !== null && now >= keeper) return 'predraft';
  if (committee === null && keeper === null && draft !== null) return 'predraft';
  return 'offseason';
}

export type OffseasonFocus = 'review' | 'checklist';

/**
 * Which face the offseason is wearing.
 *
 * `review` is the long half: the year just gone, the history boards, and a countdown to a draft
 * that is months away. `checklist` starts `CHECKLIST_LEAD_DAYS` before rule proposals close, and
 * is Tim's instruction almost verbatim — the deadlines come to the front, rule submission is the
 * headline, keeper lists surface, the year in review goes, history drops to secondary.
 *
 * Outside the offseason this returns `checklist`, because every later phase has work due and
 * nothing is asking to look backwards. Callers that only care about the offseason gate it on the
 * phase first; the value is honest either way.
 */
export function offseasonFocus(
  s: PhaseInput | null | undefined,
  now = Date.now(),
): OffseasonFocus {
  if (phaseOf(s, now) !== 'offseason') return 'checklist';
  /*
   * The proposal deadline is the one this hangs off, because it is the one the checklist is
   * about. With none announced, the committee date and the keeper deadline are the next two
   * things that would make a checklist worth reading — a league that has announced neither has
   * nothing to put on one.
   */
  const lead = CHECKLIST_LEAD_DAYS * DAY_MS;
  for (const iso of [s?.proposalDeadline, s?.committeeDate, s?.keeperDeadline]) {
    const t = at(iso);
    if (t !== null) return now >= t - lead ? 'checklist' : 'review';
  }
  return 'review';
}

/* ------------------------------------------------------------------ the draft the app counts to */

/**
 * Labor Day: the first Monday in September, as UTC midnight of that date.
 *
 * Built by walking from the 1st rather than by arithmetic on the weekday, because the arithmetic
 * version is off by a week whenever the 1st is itself a Monday and nobody notices until the year
 * it happens.
 */
export function laborDay(year: number): Date {
  const d = new Date(Date.UTC(year, 8, 1));
  while (d.getUTCDay() !== 1) d.setUTCDate(d.getUTCDate() + 1);
  return d;
}

/**
 * The league's draft day when nobody has announced one: the Tuesday after Labor Day, 8:00 PM ET.
 *
 * `-04:00` RATHER THAN A ZONE LOOKUP, and it is safe for exactly this date. Labor Day is the first
 * Monday in September and the day after it is therefore between 2 and 8 September — months inside
 * daylight time, which does not end until November. This is the one date in the app where the
 * offset can be written down; anything a commissioner types goes through `isoFromZoned`.
 *
 * 8:00 PM ET is the hour the league has drafted at, and is what the 2026 calendar says.
 */
export function projectedDraftDay(year: number): string {
  const d = laborDay(year);
  d.setUTCDate(d.getUTCDate() + 1);
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return new Date(`${d.getUTCFullYear()}-${month}-${day}T20:00:00-04:00`).toISOString();
}

export interface DraftTarget {
  iso: string;
  /** True when this is the Tuesday-after-Labor-Day guess rather than an announced date. */
  projected: boolean;
  /** Whole days away, never negative. */
  days: number;
}

const whole = (target: number, now: number) => Math.max(0, Math.ceil((target - now) / DAY_MS));

/**
 * What the countdown counts to.
 *
 * The offseason starts the day after the championship game, which is January, and at that point
 * there is no draft date in the database and will not be one for months — so a countdown that
 * needs an announced date shows nothing for half the year, on the screen whose whole job in that
 * half is to say how far away the season is. The projection fills it, and says it is a projection.
 *
 * Once this year's draft is behind us the projection rolls to next year, so the day after the
 * 2026 draft the offseason is already counting to 2027.
 */
export function draftTarget(
  draftDate: string | null | undefined,
  now = Date.now(),
): DraftTarget {
  const announced = at(draftDate);
  if (announced !== null && now < announced + DAY_MS) {
    return { iso: new Date(announced).toISOString(), projected: false, days: whole(announced, now) };
  }
  const year = new Date(now).getUTCFullYear();
  let iso = projectedDraftDay(year);
  if (now >= Date.parse(iso) + DAY_MS) iso = projectedDraftDay(year + 1);
  return { iso, projected: true, days: whole(Date.parse(iso), now) };
}

/** Whole days from now to the draft, or null when no date is set. Never negative. */
export function daysToDraft(draftDate: string | null | undefined, now = Date.now()): number | null {
  const t = at(draftDate);
  return t === null ? null : whole(t, now);
}

/* ------------------------------------------------------------------ where the season has got to */

/**
 * Which week of the regular season it is, or null when the app cannot honestly say.
 *
 * The header's countdown slot has to name something true in every phase, and "WEEK 6" is what it
 * says all autumn. There is nowhere in the league's data that stores a week number, so it is
 * derived: this league drafts on the Tuesday after Labor Day, and the NFL's week 1 opens the
 * Thursday of that same week, so week 1 is the seven days beginning two days after the draft.
 *
 * NULL RATHER THAN A GUESS in three cases, because a wrong week number on every screen is worse
 * than an empty slot: no announced draft date, a date the app only projected, and any moment
 * before week 1 opens or after the regular season has run out. Blank beats partial.
 *
 * `weeks` is the league's own regular season (14 by 2021-I-1 §1); the NFL plays past it and the
 * fantasy playoffs run inside those weeks, which is why the cap is the NFL's 18 rather than 14 —
 * a manager in December is still in a week, he is just not in a matchup this function knows about.
 */
export function seasonWeek(
  draftDate: string | null | undefined,
  now = Date.now(),
  weeks = 18,
): number | null {
  const drafted = at(draftDate);
  if (drafted === null) return null;
  /* Week 1 opens on the Thursday two days after a Tuesday draft. */
  const opens = drafted + 2 * DAY_MS;
  if (now < opens) return null;
  const week = Math.floor((now - opens) / (7 * DAY_MS)) + 1;
  return week >= 1 && week <= weeks ? week : null;
}
